import { AxiosResponse, isAxiosError } from 'axios';
import { createLogger } from 'logging';

import {
    CreateUserRequest,
    UpdateAbuseStatus200Response,
    UpdateAbuseStatusRequest,
    UpdateUserRequest,
    User,
    UserAbuseLog,
    Permission,
    UserYoutubeChannel,
} from '@videoblocks/user-service-client';

import { CreateUserOutcome, CreateUserResult } from './CreateUserResult';
import { isErrorMessage } from './isErrorMessage';
import { userApi } from './userServiceConfig';

const logger = createLogger('UserApi');

const createUser = async (createUserRequest: CreateUserRequest): Promise<CreateUserResult> => {
    try {
        const result = (await userApi.createUser({ createUserRequest })) as AxiosResponse<User>;
        return { outcome: CreateUserOutcome.UserCreated, user: result.data };
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error?.response ?? {};
            if (status === 302) {
                return { outcome: CreateUserOutcome.UserAlreadyRegistered };
            }
            if (status === 403 || status === 409) {
                const data: unknown = error.response?.data;
                const errorMessage = isErrorMessage(data) ? data.message : error.message;
                return { outcome: CreateUserOutcome.Error, error: errorMessage };
            }
        }
        throw error;
    }
};

const getUserBySession = async (sessionToken: string): Promise<User | null> => {
    try {
        const result = (await userApi.searchUsers({ session: sessionToken })) as AxiosResponse<User>;
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
    }
    return null;
};

const checkUserPermissionByNameAndResource = async (
    userId: number,
    permission: string,
    resource: string,
): Promise<boolean> => {
    try {
        const permissions = await getUserPermissions(userId);
        return permissions.some((perm) => perm.permissionName === permission && perm.resource === resource);
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
        return false;
    }
};

const getUserPermissionValueByNameAndResource = async (
    userId: number,
    permission: string,
    resource: string,
): Promise<string | null> => {
    try {
        const permissions = await getUserPermissions(userId);
        const requestedPermission = permissions.find(
            (perm) => perm.permissionName === permission && perm.resource === resource,
        );
        return requestedPermission?.value ?? null;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
        return null;
    }
};

const getUserPermissions = async (userId: number): Promise<Permission[]> => {
    try {
        const result = (await userApi.getPermissions({ userId: userId })) as AxiosResponse<Permission[]>;
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status !== 404) {
                throw error;
            }
        }
        return [];
    }
};

const getUserById = async (userId: number): Promise<User | null> => {
    try {
        const result = (await userApi.getUserById({ userId })) as AxiosResponse<User>;
        return result.data;
    } catch (error) {
        if (isAxiosError(error)) {
            const { status } = error.response || {};
            if (status === 404) {
                return null; // User not found
            }
        }
        throw error;
    }
};

const updateUser = async (userId: number, updateData: UpdateUserRequest): Promise<User> => {
    const result = (await userApi.updateUser({
        userId,
        updateUserRequest: updateData,
    })) as AxiosResponse<User>;
    return result.data;
};

const getUserYoutubeChannel = async (userId: number): Promise<UserYoutubeChannel[]> => {
    const result = (await userApi.getUserYoutubeChannels({ userId })) as AxiosResponse<UserYoutubeChannel[]>;
    return result.data;
};

const updateUserYoutubeChannel = async (userId: number, youtubeChannels: string[]): Promise<UserYoutubeChannel[]> => {
    const [youtubeChannel1, youtubeChannel2] = youtubeChannels;
    try {
        const result = (await userApi.updateUserYoutubeChannel({
            userId,
            updateUserYoutubeChannelRequest: {
                youtube_channel_id_1: youtubeChannel1,
                youtube_channel_id_2: youtubeChannel2,
            },
        })) as AxiosResponse<UserYoutubeChannel[]>;
        return result.data;
    } catch (err) {
        if (isAxiosError(err)) {
            const responseData: unknown = err.response?.data;
            const errorMessage = isErrorMessage(responseData) ? responseData.message : err.message;
            throw new Error(errorMessage);
        }
        throw err;
    }
};

const getAbuseLog = async (userId: number): Promise<UserAbuseLog | null> => {
    try {
        const result = (await userApi.getAbuseLogsByUserId({ userId })) as AxiosResponse<UserAbuseLog>;
        return result.data;
    } catch (e: unknown) {
        if (isAxiosError(e)) {
            if (e.response?.status === 404) {
                logger.info('No abuse status found for the user.', e.message);
                return null;
            }
        }
        throw e;
    }
};

const updateAbuseStatus = async (
    request: UpdateAbuseStatusRequest,
): Promise<{ success: boolean; message?: string }> => {
    try {
        const result = (await userApi.updateAbuseStatus({
            updateAbuseStatusRequest: request,
        })) as AxiosResponse<UpdateAbuseStatus200Response>;
        return {
            success: result.data.success ?? false,
            message: result.data.message,
        };
    } catch (e: unknown) {
        if (isAxiosError(e) && e.response && e.response.status === 400) {
            const responseData: unknown = e.response.data;
            const message = isErrorMessage(responseData) ? responseData.message : e.message;
            logger.warn('Error updating abuse status. message: %s', message);
            return { success: false, message };
        }
        throw e;
    }
};

export const UserApi = {
    createUser,
    getUserBySession,
    getUserById,
    updateUser,
    getUserYoutubeChannel,
    updateUserYoutubeChannel,
    getAbuseLog,
    updateAbuseStatus,
    getUserPermissions,
    checkUserPermissionByNameAndResource,
    getUserPermissionValueByNameAndResource,
};
